import React from "react";
import { Meta } from "../layout/EducationMeta";
import GlassCard from "../components/ui/GlassCard";
import Link from "../components/Link";
import { SITE } from "../config/site";
import { LEGAL_ENTITY, LEGAL_SHARED } from "../config/legal";

const PAGE = "mx-auto w-full max-w-[110rem] px-4 md:px-6 lg:px-8 xl:px-10 2xl:px-12";

function Block({ block }) {
  if (typeof block === "string") {
    return <p>{block}</p>;
  }
  if (block.type === "bullets") {
    return (
      <ul className="space-y-2">
        {block.items.map((item) => (
          <li key={item} className="flex gap-3">
            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-hint-strong" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    );
  }
  if (block.type === "warning") {
    return (
      <p className="rounded-2xl border border-ink/8 bg-bg1 px-4 py-3 font-medium text-ink/80">
        {block.text}
      </p>
    );
  }
  if (block.type === "internalLink") {
    return (
      <p>
        <Link to={block.to} className="font-semibold text-hint-deep underline underline-offset-4 hover:text-ink">
          {block.label}
        </Link>
      </p>
    );
  }
  if (block.type === "link") {
    return (
      <p>
        <a
          href={block.href}
          className="font-semibold text-hint-deep underline underline-offset-4 hover:text-ink"
          target={block.href.startsWith("mailto:") ? undefined : "_blank"}
          rel={block.href.startsWith("mailto:") ? undefined : "noopener noreferrer"}
        >
          {block.label}
        </a>
      </p>
    );
  }
  return null;
}

export default function LegalDocumentPage({
  badge,
  title,
  description,
  sections,
  relatedLinks = [],
  path,
  founderDraft = true,
}) {
  return (
    <div className={`${PAGE} py-12 md:py-16`}>
      <Meta title={`${title} — Stratify Backtesting`} description={description} path={path} />

      <div className="max-w-4xl">
        <div className="inline-flex items-center rounded-full border border-hint/25 bg-hint/10 px-3 py-1 text-xs font-semibold tracking-wide text-hint-deep">
          {badge}
        </div>
        <h1 className="mt-5 text-3xl font-semibold tracking-tight text-ink md:text-5xl">{title}</h1>
        <p className="mt-4 text-sm leading-relaxed text-muted md:text-base">{description}</p>
        {founderDraft ? (
          <p className="mt-3 text-sm text-muted">{LEGAL_SHARED.founderDraft}</p>
        ) : null}
      </div>

      <div className="mt-8 grid gap-5 lg:grid-cols-3">
        <GlassCard className="p-5">
          <div className="text-xs font-semibold uppercase tracking-[0.16em] text-muted">Publication date</div>
          <div className="mt-2 text-lg font-semibold text-ink">{SITE.legalEffectiveDate}</div>
        </GlassCard>
        <GlassCard className="p-5">
          <div className="text-xs font-semibold uppercase tracking-[0.16em] text-muted">Company</div>
          <div className="mt-2 text-lg font-semibold text-ink">{LEGAL_ENTITY}</div>
        </GlassCard>
        <GlassCard className="p-5">
          <div className="text-xs font-semibold uppercase tracking-[0.16em] text-muted">Contact</div>
          <a
            href={`mailto:${SITE.supportEmail}`}
            className="mt-2 inline-flex break-all text-lg font-semibold text-hint-deep transition hover:text-ink"
          >
            {SITE.supportEmail}
          </a>
        </GlassCard>
      </div>

      <div className="mt-10 grid gap-8 lg:grid-cols-12">
        <aside className="lg:col-span-3">
          <GlassCard className="sticky top-6 p-5">
            <div className="text-xs font-semibold uppercase tracking-[0.16em] text-muted">On this page</div>
            <nav className="mt-4 space-y-2 text-sm">
              {sections.map((section) => (
                <a
                  key={section.id}
                  href={`#${section.id}`}
                  className="block rounded-lg px-3 py-2 text-muted transition hover:bg-bg1 hover:text-ink"
                >
                  {section.title}
                </a>
              ))}
            </nav>
          </GlassCard>
        </aside>

        <main className="lg:col-span-9">
          <GlassCard className="p-6 md:p-8">
            <div className="space-y-8">
              {sections.map((section) => (
                <section
                  key={section.id}
                  id={section.id}
                  className="scroll-mt-28 border-t border-ink/8 pt-8 first:border-t-0 first:pt-0"
                >
                  <h2 className="text-xl font-semibold tracking-tight text-ink md:text-2xl">{section.title}</h2>
                  <div className="mt-4 space-y-4 text-sm leading-relaxed text-muted md:text-[15px]">
                    {section.blocks.map((block, index) => (
                      <Block key={`${section.id}-${index}`} block={block} />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </GlassCard>

          {relatedLinks.length ? (
            <div className="mt-5 flex flex-wrap gap-3">
              {relatedLinks.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className="inline-flex items-center justify-center rounded-xl border border-ink/8 bg-bg1 px-4 py-3 text-sm font-semibold text-ink/80 transition hover:bg-bg1 hover:text-ink"
                >
                  {item.label} →
                </Link>
              ))}
            </div>
          ) : null}
        </main>
      </div>
    </div>
  );
}
